import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Card,
  FilterSelect,
  LineChart,
  SectionLabel,
  Spinner,
  StatCard,
  type FilterOption,
} from '@wundo/ui';
import type { SkillRecommendationStatusCount } from '@wundo/shared/skillRecommendationStats.ts';
import { fetchSkillRecommendationStats } from '@/lib/skillRecommendations.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';

const WINDOW_OPTIONS: readonly FilterOption<string>[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
];

/* Fixed order, so the bars mean the same thing from one window to the next. */
const STATUS_ORDER: SkillRecommendationStatusCount['status'][] = [
  'pending',
  'delivered',
  'skipped',
  'superseded',
  'expired',
];

/**
 * How the daily skill recommendations are doing, over the chosen window.
 *
 * It sits above the run list because a single run only makes sense against the
 * shape of the whole: one skipped outreach is noise, a week of them is a bug.
 */
export function SkillRecommendationStatsHeader() {
  const [windowDays, setWindowDays] = useState(7);

  const {
    data: stats,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['skillRecommendationStats', windowDays],
    queryFn: () => fetchSkillRecommendationStats(windowDays),
  });

  const statusCounts = useMemo(() => {
    const byStatus = new Map(
      (stats?.status_counts ?? []).map((one) => [one.status, one.count]),
    );
    return STATUS_ORDER.map((status) => byStatus.get(status) ?? 0);
  }, [stats]);

  const deliverRate =
    stats && stats.total_in_window > 0
      ? Math.round((stats.delivered_in_window / stats.total_in_window) * 100)
      : 0;

  const skippedCount =
    stats?.status_counts.find((one) => one.status === 'skipped')?.count ?? 0;

  return (
    <section className='flex flex-col gap-4'>
      {/* On a phone the window picker goes under the title: beside it, it shrinks to "Last …". */}
      <div className='flex items-start justify-between gap-3 max-mobile:flex-col'>
        <div>
          <SectionLabel as='h2'>Recommendation health</SectionLabel>
          <p className='mt-1 text-xs text-ink-muted'>
            Outreach lifecycle for the selected window.
          </p>
        </div>
        <FilterSelect
          hideLabel
          label='Window'
          options={WINDOW_OPTIONS}
          value={String(windowDays)}
          onValueChange={(days) => setWindowDays(Number(days))}
        />
      </div>

      {isPending && (
        <Card>
          <div className='flex items-center gap-2 py-3 text-sm font-semibold text-ink-muted'>
            <Spinner />
            Loading…
          </div>
        </Card>
      )}

      {isError && (
        <Card>
          <p role='alert' className='text-sm font-semibold text-danger'>
            Failed to load stats
          </p>
        </Card>
      )}

      {stats && (
        <>
          <div className='grid grid-cols-4 gap-3 max-mobile:grid-cols-2'>
            <StatCard
              label='Last outreach'
              value={
                stats.last_recommended_at
                  ? formatDateTime(stats.last_recommended_at)
                  : 'never'
              }
            />
            <StatCard
              label='Outreaches'
              value={stats.total_in_window}
              hint={`last ${stats.window_days}d`}
            />
            <StatCard
              label='Delivered'
              value={stats.delivered_in_window}
              hint={`${deliverRate}% rate`}
            />
            <StatCard
              label='Skipped'
              value={skippedCount}
              hint={`${stats.skip_reasons.length} reasons`}
            />
          </div>

          <div className='grid grid-cols-3 gap-4 max-agenda:grid-cols-1'>
            <Card>
              <SectionLabel as='h3' className='mb-2'>
                Status breakdown
              </SectionLabel>
              <BarChart
                ariaLabel='Outreaches by status'
                labels={STATUS_ORDER}
                series={[{ label: 'Count', data: statusCounts }]}
                height={192}
                emptyTitle='No outreaches yet.'
              />
            </Card>

            <Card>
              <SectionLabel as='h3' className='mb-2'>
                Skip reasons
              </SectionLabel>
              <BarChart
                ariaLabel='Skipped outreaches by reason'
                labels={stats.skip_reasons.map((one) => one.reason)}
                series={[
                  {
                    label: 'Skipped',
                    data: stats.skip_reasons.map((one) => one.count),
                    color: 'plum',
                  },
                ]}
                height={192}
                emptyTitle='None skipped.'
              />
            </Card>

            <Card>
              <SectionLabel as='h3' className='mb-2'>
                Daily delivery
              </SectionLabel>
              <LineChart
                ariaLabel='Outreaches delivered per day'
                labels={stats.daily.map((point) => point.day.slice(5))}
                series={[
                  {
                    label: 'Delivered',
                    data: stats.daily.map((point) => point.delivered),
                  },
                  { label: 'Total', data: stats.daily.map((point) => point.total) },
                ]}
                height={192}
                emptyTitle='No data.'
              />
            </Card>
          </div>
        </>
      )}
    </section>
  );
}
